import { type LessonSummary } from '@/platform/learning/contract'
import { type LearningService } from '@/platform/learning/service'

import { useFocusOnMount } from './focus'
import { type Subject } from './subjects'
import { TodayLesson } from './TodayLesson'

/**
 * Subject pages reached from Child Home (Slice 1 product shell). English
 * reuses today's lesson from `LearningService`; the other subjects say plainly
 * that they are not available yet (D-14: P0 direction, not implemented in this
 * slice). Nothing here is simulated. School materials have their own journey
 * (`../materials/SchoolMaterialJourney.tsx`).
 */

function ChildPage({
  icon,
  title,
  onBack,
  children,
}: {
  icon: string
  title: string
  onBack: () => void
  children: React.ReactNode
}): React.JSX.Element {
  const heading = useFocusOnMount<HTMLHeadingElement>()
  return (
    <div className="mx-auto flex w-full max-w-md flex-1 flex-col gap-6 px-5 pt-2 pb-16 md:max-w-2xl">
      <button
        type="button"
        className="self-start px-1 text-lg text-sky-800 underline dark:text-sky-300"
        onClick={onBack}
      >
        ← Về trang chính
      </button>
      <div className="flex flex-col items-center gap-2 text-center">
        <p className="text-6xl" aria-hidden>
          {icon}
        </p>
        <h1 ref={heading} tabIndex={-1} className="text-3xl font-bold">
          {title}
        </h1>
      </div>
      {children}
    </div>
  )
}

function ComingSoon({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-slate-300 p-6 text-center dark:border-slate-700">
      <p className="rounded-full bg-amber-100 px-3 py-1 text-base font-semibold text-amber-900 dark:bg-amber-950 dark:text-amber-200">
        Sắp có
      </p>
      {children}
    </div>
  )
}

export interface SubjectPageProps {
  readonly subject: Subject
  readonly learning: LearningService
  readonly learnerId: string
  readonly inProgressLessonId: string | null
  readonly starting: boolean
  readonly startFailed: boolean
  readonly onStart: (lesson: LessonSummary) => void
  readonly onBack: () => void
  /** Offered when DEMO school materials exist (local/development only). */
  readonly onTrySample?: (() => void) | null
}

export function SubjectPage({
  subject,
  learning,
  learnerId,
  inProgressLessonId,
  starting,
  startFailed,
  onStart,
  onBack,
  onTrySample = null,
}: SubjectPageProps): React.JSX.Element {
  return (
    <ChildPage icon={subject.icon} title={subject.name} onBack={onBack}>
      {subject.available ? (
        <div className="flex flex-col items-center gap-3 text-center">
          <TodayLesson
            learning={learning}
            learnerId={learnerId}
            inProgressLessonId={inProgressLessonId}
            starting={starting}
            startFailed={startFailed}
            onStart={onStart}
          />
        </div>
      ) : (
        <ComingSoon>
          <p className="text-lg">Môn {subject.name} đang được chuẩn bị.</p>
          <p className="text-base text-slate-700 dark:text-slate-300">
            Hiện chưa có bài học. Khi có bài, bài sẽ hiện ở đây.
          </p>
          {onTrySample !== null && (
            <>
              <p className="text-base text-slate-700 dark:text-slate-300">
                Trong lúc chờ, con có thể thử học từ một tài liệu mẫu môn {subject.name}{' '}
                (DEMO).
              </p>
              <button
                type="button"
                className="h-12 rounded-xl bg-sky-700 px-5 text-lg font-semibold text-white"
                onClick={onTrySample}
              >
                Thử tài liệu mẫu
              </button>
            </>
          )}
        </ComingSoon>
      )}
    </ChildPage>
  )
}
